import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import * as assets from '@assets';
import {
  CapaActivityType,
  CapaDesignService,
  CapaDesignServiceType,
  DesignServiceInput,
  useFetchQuotationUserRfqDetailQuery
} from '@generated/graphql';
import useQueryString from '@hooks/useQueryString';
import {
  DesignRequestStageEnum,
  RequestQueryString
} from '@routes/NewRequest/types';
import useRequest from '@hooks/useRequest';
import NewRfqRewriteDialog from '../Dialogs/NewRfqRewriteDialog';
import useUserActionLog, {
  Enum_Action_Page_Enum
} from '@hooks/useUserActionLog';

export interface DesignProcessSelectProps {
  setDisabledNextButton: ((value: boolean) => void) | null;
  nextButtonAction: React.MutableRefObject<
    Partial<Record<DesignRequestStageEnum, () => void>>
  >;
}

const processList = [
  {
    title: '제품 디자인',
    description: '심미성을 고려해 제품의 외관을 디자인합니다.',
    enum: CapaDesignServiceType.ProductDesign,
    image: assets.ServiceType_Pd_192
  },
  {
    title: '기구설계',
    description: '제품이 구현되도록 제조성을 고려해 설계합니다.',
    enum: CapaDesignServiceType.MechanicalDesign,
    image: assets.ServiceType_Md_192
  }
];

const DesignProcessSelect: React.FC<DesignProcessSelectProps> = ({
  setDisabledNextButton,
  nextButtonAction
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const { onUpdateRfqInfo, preRfqId } = useRequest();
  const {
    query,
    query: { process },
    onUpdateQueryString
  } = useQueryString<RequestQueryString>();
  const processArray = process?.split(',') as CapaDesignServiceType[];
  const [selectedProcess, setSelectedProcess] = useState<
    CapaDesignServiceType[]
  >(processArray ?? []);
  const { selectContainer, processEntry } = usePCBProcessSelectStyles();

  const onClickProcessCard = (value: CapaDesignServiceType) => {
    if (selectedProcess.includes(value)) {
      setSelectedProcess((prev) => prev.filter((el) => el !== value));
    } else {
      setSelectedProcess((prev) => [...prev, value]);
    }
  };

  const reWrite = query.reWrite === 'true';
  const rfqId = query.rfqId;
  const [rfqRewirteDialogOpen, setRfqRewirteDialogOpen] = useState(reWrite);

  const { onInsertUserActionLogOne } = useUserActionLog();

  useEffect(() => {
    if (reWrite) {
      onInsertUserActionLogOne(
        Enum_Action_Page_Enum.Page_RFQ_Rewrite_DESIGN_ProcessMethod,
        CapaActivityType.PageVisit
      );
    }
  }, [onInsertUserActionLogOne, reWrite]);

  useEffect(() => {
    if (!setDisabledNextButton) return;

    if (selectedProcess.length === 0) {
      setDisabledNextButton(true);
      onUpdateQueryString({
        process: undefined
      });
    } else {
      setDisabledNextButton(false);
      onUpdateQueryString({
        process: selectedProcess.toString()
      });
    }
  }, [selectedProcess, setDisabledNextButton, onUpdateQueryString]);

  useEffect(() => {
    onUpdateRfqInfo({
      detailedServiceType: null,
      design: {
        services: selectedProcess.map(
          (process) =>
            ({
              serviceType: process as CapaDesignService
            } as DesignServiceInput)
        )
      }
    });
    //eslint-disable-next-line
  }, [selectedProcess, onUpdateRfqInfo]);

  const { data } = useFetchQuotationUserRfqDetailQuery({
    variables: {
      rfqId: rfqId ?? ''
    },
    fetchPolicy: 'network-only'
  });

  const referenceFiles =
    data?.fetchQuotationUserRfqDetail?.data?.referenceFiles ?? [];

  const setReWriteRfqInfo = () => {
    setRfqRewirteDialogOpen((prev) => false);

    if (referenceFiles.length === 0) {
      onUpdateQueryString({
        reWrite: undefined,
        rfqId: undefined
      });
      return;
    }

    onUpdateRfqInfo({
      preRfqId: rfqId,
      referenceFiles
    });
  };

  return (
    <>
      {!preRfqId && (
        <NewRfqRewriteDialog
          open={rfqRewirteDialogOpen}
          onClose={() => setReWriteRfqInfo()}
        />
      )}

      <div className={selectContainer}>
        <CustomizedTypography
          variant="body1"
          color="textPrimary"
          gutterBottoms={16}
        >
          (중복선택 가능)
        </CustomizedTypography>

        <div className="select-list">
          {processList.map((process) => (
            <div
              key={process.enum}
              className={clsx([
                processEntry,
                {
                  selected: selectedProcess.includes(process.enum)
                }
              ])}
              onClick={(e) => {
                onClickProcessCard(process.enum);
              }}
              data-testid={`select-button-${process.enum}`}
              data-cy={`select-button-${process.enum}`}
            >
              <div className="process-entry-text">
                <CustomizedTypography
                  variant={!matches ? 'h4' : 'subtitle1'}
                  color="textPrimary"
                  fontWeight="700"
                  gutterBottoms={matches ? 2 : undefined}
                >
                  {process.title}
                </CustomizedTypography>
                <CustomizedTypography
                  variant={!matches ? 'body1' : 'body2'}
                  color="textPrimary"
                >
                  {process.description}
                </CustomizedTypography>
              </div>
              <img
                className="process-entry-image"
                src={process.image}
                alt={process.title}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default DesignProcessSelect;

const usePCBProcessSelectStyles = makeStyles((theme) => ({
  selectContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    '& > p': {
      width: '100%'
    },
    '& .select-list': {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    },
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
      height: '100%'
    }
  },
  processEntry: {
    width: '100%',
    height: '111px',
    padding: '24px',
    borderRadius: '16px',
    border: `1px solid ${colorSet.gray400}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colorSet.gray000,
    textAlign: 'left',
    //eslint-disable-next-line
    ['@media (hover: hover)']: {
      '&:hover': {
        cursor: 'pointer',
        border: `2px solid ${colorSet.gray900}`,
        padding: '23px',
        [theme.breakpoints.down('sm')]: {
          border: `1px solid ${colorSet.gray400}`,
          padding: '24px'
        }
      }
    },
    '&.selected': {
      border: `2px solid ${colorSet.gray900}`,
      backgroundColor: colorSet.gray100,
      padding: '23px'
    },
    '& .process-entry-text': {
      display: 'flex',
      flexDirection: 'column'
    },
    '& .process-entry-image': {
      width: '79px',
      height: '79px',
      borderRadius: '16px',
      [theme.breakpoints.down('sm')]: {
        width: '64px',
        height: '64px'
      }
    }
  }
}));
